import { ReactNode, SyntheticEvent } from 'react';
import {
  Autocomplete as MUIAutocomplete,
  AutocompleteProps as MUIAutocompleteProps,
  Popper as MUIPopper,
} from '@mui/material';

import {
  Icon,
  TextField,
  TextFieldProps as TextFieldPropsType,
} from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';

type CustomProps = {
  label?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  /**
   * popperAutoWidth if true will cause the select list's
   * width to grow dynamically with the content of each list item
   */
  popperAutoWidth?: boolean;
  autoFocus?: boolean;
  error?: boolean;
  errorHelperText?: string;
  TextFieldProps?: TextFieldPropsType;
  placeholder?: string;
};

type AtomMUIProps = {
  id?: string;
  disableClearable?: boolean;
  clearIcon?: ReactNode;
  classes?: any;
  multiple?: boolean;
  loading?: boolean;
  disabled?: boolean;
  inputValue?: string;
  onInputChange?: (event: SyntheticEvent<any>, value: string) => void;
  getOptionLabel?: (option: any) => string;
  getOptionDisabled?: (option: any) => boolean;
  renderOption?: (props: object, option: any) => ReactNode;
  renderValue?: (values: any[]) => ReactNode;
  renderInput?: any;
  value?: any;
  onChange?: (event: SyntheticEvent<any>, value: any) => void;
  onClick?: () => void;
  filterOptions?: (options: Array<any>, state: object) => Array<any>;
  disableCloseOnSelect?: boolean;
  groupBy?: (options: any) => string;
  clearOnBlur?: boolean;
  noOptionsText?: ReactNode;
  renderGroup?: (params: any) => ReactNode;
  options: any[];
};

type AutocompleteProps = CustomProps & AtomMUIProps;

const styles = {
  popper: {
    maxWidth: 'fit-content',
  },
  autocomplete: {
    '& .MuiAutocomplete-groupLabel': {
      color: colors.neutral.gray,
      textTransform: 'uppercase',
      fontSize: fonts.sm,
    },
    '& .MuiAutocomplete-clearIndicator': {
      padding: '4px',
    },
  },
  endAdornment: {
    '& .MuiAutocomplete-endAdornment': {
      top: 'calc(50% - 18px)',
    },
  },
};

export const Autocomplete = (props: AutocompleteProps) => {
  const {
    label,
    placeholder,
    startAdornment,
    endAdornment,
    disableClearable = false,
    clearIcon,
    popperAutoWidth,
    autoFocus,
    error,
    errorHelperText,
    TextFieldProps,
    multiple = false,
    ...data
  } = props;

  const MUIProps: Omit<
    MUIAutocompleteProps<any, any, any, any>,
    'renderInput' | 'autoComplete'
  > = data;

  return (
    <MUIAutocomplete
      freeSolo
      multiple={multiple}
      disableClearable={disableClearable ?? !!endAdornment}
      {...(popperAutoWidth && {
        PopperComponent: popperProps => (
          <MUIPopper
            {...popperProps}
            style={styles.popper}
            placement="bottom-start"
          />
        ),
      })}
      renderInput={params => (
        <TextField
          {...params}
          {...TextFieldProps}
          error={error}
          helperText={error && errorHelperText}
          autoFocus={autoFocus}
          label={label}
          placeholder={placeholder}
          inputLabelColor={colors.neutral.dim}
          slotProps={{
            htmlInput: params.inputProps,
            inputLabel: { ...params.InputLabelProps },
            input: {
              ...params.InputProps,
              ...(!!startAdornment && { startAdornment }),
              ...(!!endAdornment && { endAdornment }),
            },
          }}
        />
      )}
      clearIcon={clearIcon || <Icon>close</Icon>}
      {...MUIProps}
      sx={{
        ...styles.autocomplete,
        ...(!endAdornment && styles.endAdornment),
      }}
      classes={props.classes}
    />
  );
};

export default Autocomplete;
